/** One reload after a stale deploy asks for a hashed file that is gone. */
export const CHUNK_RELOAD_KEY = 'fc-chunk-reload';

export function chunkFailureText(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return `${value.name || ''} ${value.message || ''}`.trim();
}

export function isChunkLoadFailure(value) {
  const text = chunkFailureText(value);
  if (!text) return false;
  if (value && value.name === 'ChunkLoadError') return true;
  if (/ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module|Importing a module script failed/i.test(text)) {
    return true;
  }
  return text.indexOf('Unexpected token') !== -1 && text.indexOf('<') !== -1;
}

export function isSameOriginStaticUrl(raw, origin) {
  if (!raw || !origin) return false;
  try {
    const url = new URL(raw, origin);
    return url.origin === origin && url.pathname.startsWith('/static/');
  } catch {
    return false;
  }
}

export function isAppAssetElement(target, origin) {
  if (!target || !target.tagName) return false;
  if (target.tagName !== 'SCRIPT' && target.tagName !== 'LINK') return false;
  return isSameOriginStaticUrl(target.src || target.href || '', origin);
}

/**
 * Returns true when this failure should reload.
 * A third-party script error is ignored. A same-origin /static asset
 * (or a chunk/parse error from one) is not.
 */
export function shouldRecoverFromAssetFailure({ target, error, message, filename, origin }) {
  if (isAppAssetElement(target, origin)) return true;
  const failed = isChunkLoadFailure(error) || isChunkLoadFailure(message);
  if (!failed) return false;
  if (!filename) return true;
  return isSameOriginStaticUrl(filename, origin);
}

/**
 * Reloads once per tab session. The flag is set before reload, so a second
 * failure on the reloaded page cannot loop.
 * Returns true when a reload was started.
 */
export function reloadOnceForChunkError(storage, reload) {
  const store = storage || (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  const go = reload || (() => window.location.reload());
  if (!store) return false;
  try {
    if (store.getItem(CHUNK_RELOAD_KEY)) return false;
    store.setItem(CHUNK_RELOAD_KEY, '1');
  } catch {
    return false;
  }
  go();
  return true;
}

export function installChunkReloadGuard(win = window) {
  if (!win || win.__fcChunkReloadInstalled) return;
  win.__fcChunkReloadInstalled = true;
  const origin = win.location.origin;
  const recover = (details) => {
    if (!shouldRecoverFromAssetFailure({ ...details, origin })) return;
    reloadOnceForChunkError(win.sessionStorage, () => win.location.reload());
  };
  win.addEventListener('error', (event) => {
    recover({
      target: event.target,
      error: event.error,
      message: event.message,
      filename: event.filename,
    });
  }, true);
  win.addEventListener('unhandledrejection', (event) => {
    recover({ error: event.reason, message: event.reason });
  });
}
